import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Family, FamilyInput } from '@pirouette/shared';
import { familyInputSchema } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { stripUndefined } from '@/lib/db';
import { queryKeys } from '@/lib/queryClient';

// Family-list filters supported by the admin UI.
export interface FamilyListQuery {
  search?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useFamilies(query: FamilyListQuery = {}) {
  return useQuery({
    queryKey: [...queryKeys.families, query],
    queryFn: async (): Promise<{ rows: Family[]; total: number }> => {
      let q = supabase
        .from('families')
        .select('*', { count: 'exact' })
        .is('deleted_at', null)
        .order('primary_guardian_name', { ascending: true });

      if (query.search && query.search.trim() !== '') {
        const term = `%${query.search.trim()}%`;
        q = q.or(`primary_guardian_name.ilike.${term},primary_guardian_email.ilike.${term}`);
      }
      if (query.limit !== undefined) {
        q = q.range(query.offset ?? 0, (query.offset ?? 0) + query.limit - 1);
      }
      const { data, error, count } = await q;
      if (error) throw error;
      return { rows: (data ?? []) as unknown as Family[], total: count ?? 0 };
    },
  });
}

export function useFamily(id: string | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['family', id],
    queryFn: async (): Promise<Family> => {
      const { data, error } = await supabase
        .from('families')
        .select('*')
        .eq('id', id!)
        .is('deleted_at', null)
        .single();
      if (error) throw error;
      return data as unknown as Family;
    },
  });
}

export function useCreateFamily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: FamilyInput): Promise<Family> => {
      // Parse boundary input — never trust the caller's shape, even though
      // the form uses the same schema. Belt and suspenders.
      const parsed = familyInputSchema.parse(input);
      // billing_email is optional on input but NOT NULL in the DB: default it
      // to the guardian email when blank (mirrors the CSV import behavior).
      const row = {
        ...parsed,
        billing_email: parsed.billing_email ?? parsed.primary_guardian_email,
      };
      const { data, error } = await supabase
        .from('families')
        .insert(stripUndefined(row))
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as Family;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.families });
    },
  });
}

export function useUpdateFamily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<FamilyInput>): Promise<Family> => {
      const { id, ...rest } = input;
      // Belt-and-suspenders: parse the partial payload too — guards against
      // empty {} updates and stray fields. Mirrors useCreateFamily.
      const parsed = familyInputSchema.partial().parse(rest);
      const { data, error } = await supabase
        .from('families')
        .update(stripUndefined(parsed))
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as Family;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: queryKeys.families });
      void qc.invalidateQueries({ queryKey: ['family', data.id] });
    },
  });
}

export function useSoftDeleteFamily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const { error } = await supabase
        .from('families')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      void qc.invalidateQueries({ queryKey: queryKeys.families });
      // Also bust the detail cache so /admin/families/$id doesn't show stale.
      void qc.invalidateQueries({ queryKey: ['family', id] });
    },
  });
}
